"use client";

import {useMemo, useState, useTransition} from "react";
import {useRouter} from "next/navigation";

function countLines(value: string) {
    return value
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
        .length;
}

const SAMPLE_BLACK = `I never leave home without _.
My therapist says I need less _ and more _ || 2
What ruined brunch?`;

const SAMPLE_WHITE = `bad ideas
an over-ambitious rewrite
Discord goblins
overcompensation`;

export function CustomPackForm() {
    const router = useRouter();
    const [name, setName] = useState("");
    const [blackCardsText, setBlackCardsText] = useState(SAMPLE_BLACK);
    const [whiteCardsText, setWhiteCardsText] = useState(SAMPLE_WHITE);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isPending, startTransition] = useTransition();

    const blackCount = useMemo(() => countLines(blackCardsText), [blackCardsText]);
    const whiteCount = useMemo(() => countLines(whiteCardsText), [whiteCardsText]);
    const canSubmit = name.trim().length > 0 && blackCount + whiteCount > 0;

    return (
        <section className="panel custom-pack-panel">
            <div className="section-heading">
                <div>
                    <span className="section-kicker">Private Pack Builder</span>
                    <h2>Write pack. Keep it yours.</h2>
                </div>
                <div className="pack-builder-stats">
                    <span className="pill private">Private only</span>
                    <span className="muted">{blackCount} black / {whiteCount} white</span>
                </div>
            </div>
            <p className="section-copy">
                One card per line. Multi-pick prompts can end with <code>|| 2</code>. Custom packs stay private and only show up for your account when hosting.
            </p>
            <label className="pack-builder-field">
                <span className="card-eyebrow">Pack Name</span>
                <input
                    className="join-input pack-builder-input"
                    maxLength={80}
                    onChange={(event) => setName(event.target.value)}
                    placeholder="Midnight Test Kitchen"
                    value={name}
                />
            </label>
            <div className="pack-builder-grid">
                <label className="pack-builder-field">
                    <span className="card-eyebrow">Black Cards</span>
                    <textarea
                        className="json-input compact"
                        onChange={(event) => setBlackCardsText(event.target.value)}
                        placeholder="One prompt per line"
                        spellCheck={false}
                        value={blackCardsText}
                    />
                </label>
                <label className="pack-builder-field">
                    <span className="card-eyebrow">White Cards</span>
                    <textarea
                        className="json-input compact"
                        onChange={(event) => setWhiteCardsText(event.target.value)}
                        placeholder="One response per line"
                        spellCheck={false}
                        value={whiteCardsText}
                    />
                </label>
            </div>
            <div className="pack-builder-notes">
                <span className="muted">Question cards without underscores still work. Filled spots get appended at play time.</span>
                <span className="muted">Game start still checks combined deck totals across selected packs.</span>
            </div>
            <div className="setup-footer">
                <div>
                    <strong>{blackCount + whiteCount}</strong> cards ready
                    <span className="muted"> Save now, mix with public packs later.</span>
                </div>
                <button
                    className="button button-primary"
                    disabled={isPending || !canSubmit}
                    onClick={() => {
                        startTransition(async () => {
                            setError(null);
                            setMessage(null);
                            try {
                                const response = await fetch("/api/packs/custom", {
                                    method: "POST",
                                    headers: {"content-type": "application/json"},
                                    body: JSON.stringify({name, blackCardsText, whiteCardsText})
                                });
                                const result = await response.json() as {error?: string; pack?: {name: string}};
                                if (!response.ok) {
                                    throw new Error(result.error ?? "Failed to create pack");
                                }
                                setMessage(`Saved "${result.pack?.name ?? name}" as private pack.`);
                                setName("");
                                setBlackCardsText(SAMPLE_BLACK);
                                setWhiteCardsText(SAMPLE_WHITE);
                                router.refresh();
                            } catch (cause) {
                                console.error("Failed to create custom pack.", cause);
                                setError(cause instanceof Error ? cause.message : "Failed to create custom pack");
                            }
                        });
                    }}
                    type="button"
                >
                    {isPending ? "Saving..." : "Save private pack"}
                </button>
            </div>
            {message ? <p className="inline-note">{message}</p> : null}
            {error ? <p className="error-text">{error}</p> : null}
        </section>
    );
}
